import memoizeOne from 'memoize-one'
import { l } from '@manychat/manyui'

import {
  CapiPriceValidationErrors,
  MAX_PRICE,
  MIN_PRICE,
  CONVERSION_TYPES_WITH_PRICE,
} from 'common/actions/components/ActionItem/SendCapiEventAction/constants'
import {
  CapiActionErrors,
  Action,
} from 'common/actions/components/ActionItem/SendCapiEventAction/types'
import { IContentButton } from 'common/builder/buttons/buttonInterfaces'
import ErrorMessages from 'common/builder/constants/Validation'

export const addCapiValidationErrors = (
  actionGroupErrors: CapiActionErrors,
  capiEventAction: Action,
  capiEventActionIndex: number,
) => {
  const isMonetaryType = capiEventAction.conversion_type
    ? CONVERSION_TYPES_WITH_PRICE.includes(capiEventAction.conversion_type)
    : false

  if (isMonetaryType) {
    let prop
    if (!capiEventAction.custom_data?.value && capiEventAction.custom_data?.value !== 0) {
      prop = CapiPriceValidationErrors.MISSING_PRICE
    } else if (capiEventAction.custom_data?.value) {
      if (capiEventAction.custom_data?.value < MIN_PRICE) {
        prop = CapiPriceValidationErrors.PRICE_IS_NEGATIVE
      } else if (capiEventAction.custom_data?.value >= MAX_PRICE) {
        prop = CapiPriceValidationErrors.PRICE_EXCEEDS_MAX
      }
    }

    if (prop) {
      actionGroupErrors.push({
        prop,
        message: l.getString(ErrorMessages.ACTION_CAPI_EVENT_FIELDS_REQUIRED),
        actionIndex: capiEventActionIndex,
      })
    }

    if (!capiEventAction.custom_data?.currency) {
      actionGroupErrors.push({
        prop: 'currency',
        message: l.getString(ErrorMessages.ACTION_CAPI_EVENT_FIELDS_REQUIRED),
        actionIndex: capiEventActionIndex,
      })
    }
  }

  if (!capiEventAction.conversion_type) {
    actionGroupErrors.push({
      prop: 'conversion_type',
      message: l.getString(ErrorMessages.ACTION_CAPI_EVENT_FIELDS_REQUIRED),
      actionIndex: capiEventActionIndex,
    })
  }

  if (!capiEventAction.channel) {
    actionGroupErrors.push({
      prop: 'channel',
      message: l.getString(ErrorMessages.ACTION_CAPI_EVENT_FIELDS_REQUIRED),
      actionIndex: capiEventActionIndex,
    })
  }
}

/**
 * Extracts all content between {{ and }}
 * @param text - text to extract tokens from
 */
function extractDoubleBraceTokens(text: string): string[] {
  const tokens: string[] = []
  let i = 0
  while (i < text.length) {
    const start = text.indexOf('{{', i)
    if (start === -1) break

    let j = start + 2
    let inQuote = false
    while (j < text.length) {
      const ch = text[j]
      if (ch === '"') inQuote = !inQuote
      if (!inQuote && text[j] === '}' && text[j + 1] === '}') {
        tokens.push(text.slice(start + 2, j)) // inner content without braces
        j += 2
        break
      }
      j += 1
    }
    if (j <= start + 2) break // unclosed token; stop scanning
    i = j
  }
  return tokens
}

export const checkButtonFallbacks = memoizeOne((caption: string): boolean => {
  const tokens = extractDoubleBraceTokens(caption)

  // No variables is valid
  if (tokens.length === 0) return true

  return tokens.some((raw) => {
    const inner = String(raw).trim()
    if (!inner) return true // "{{}}" is valid

    return /\|fallback:"[^"]+"/.test(inner)
  })
})

/**
 * Check if the button captions duplicate.
 * It compares captions as strings without parsing CUFs/SUFs variables and their combinations intentionally.
 * @param buttons - The buttons to check
 * @returns The buttons that have duplicate captions
 */
export const checkButtonCaptionsDuplicate = (buttons: IContentButton[]) => {
  const captionGroups = new Map<string, IContentButton[]>()

  buttons.forEach((button) => {
    const caption = button.caption.trim()

    if (!captionGroups.has(caption)) {
      captionGroups.set(caption, [])
    }

    captionGroups.get(caption)?.push(button)
  })

  return [...captionGroups.values()].filter((group) => group.length > 1).flat()
}
